/**
 * Autopilot bot: chooses the next window's net executed RUNNER quantity by beam search over the canonical
 * movement rules. Bots may play like anyone else: they can only trade. They cannot select seeds, rewrite
 * claims, or spend budgets. Used by the local e2e driver, the demo crowd and tests.
 */
import { step } from './movement.js';
import { type CourseObject, EncounterKind, type HandlerParams, type MovementParams, Route, type RunnerState } from './types.js';

export interface AutopilotGoals {
  preferRoute?: number; // Route.FORGE or Route.HARVEST
  collectShoe?: boolean;
  collectWater?: boolean;
  avoidBarriers?: boolean;
  passForgeGate?: boolean;
}

const CANDIDATES = [-300n, -220n, -150n, -90n, -40n, 0n, 40n, 90n, 150n, 220n, 300n];

function score(cross: { kind: number; hit: boolean }[], goals: AutopilotGoals, s: RunnerState, overheated: boolean): number {
  let v = 0;
  for (const c of cross) {
    if (c.kind === EncounterKind.GOLDEN_SHOE && goals.collectShoe !== false) v += c.hit ? 1000 : -50;
    if (c.kind === EncounterKind.BARRIER && goals.avoidBarriers !== false) v += c.hit ? -400 : 20;
    if (c.kind === EncounterKind.BURNING_BARRICADE && goals.avoidBarriers !== false) v += c.hit ? -500 : 30;
    if (c.kind === EncounterKind.WATER_STATION && goals.collectWater !== false) v += c.hit ? (overheated ? 400 : 120) : 0;
    if (c.kind === EncounterKind.FORGE_GATE && goals.passForgeGate) v += c.hit ? 300 : 0;
    if (c.kind === EncounterKind.FORK && goals.preferRoute !== undefined) v += s.route === goals.preferRoute ? 350 : -350;
  }
  return v;
}

/** Returns the lateral input u (units) for the next step; convert with `netForInput`. */
export function chooseInput(
  s: RunnerState,
  course: CourseObject[],
  cursor: number,
  refSupply: bigint,
  m: MovementParams,
  h: HandlerParams,
  goals: AutopilotGoals = {},
  depth = 3,
): bigint {
  const ppmUnit = refSupply / 1_000_000n;
  let bestU = 0n;
  let best = -Infinity;
  const search = (st: RunnerState, cur: number, d: number): number => {
    if (d === 0) return 0;
    let b = -Infinity;
    for (const u of CANDIDATES) {
      const net = (u * ppmUnit) / m.sensitivity;
      const out = step(st, course, cur, net, net < 0n ? -net : net, refSupply, m, h);
      const v = score(out.crossings, goals, out.state, st.overheatSteps > 0n) - Math.abs(Number(u)) * 0.05 + 0.9 * search(out.state, out.cursor, d - 1);
      if (v > b) b = v;
    }
    return b;
  };
  for (const u of CANDIDATES) {
    const net = (u * ppmUnit) / m.sensitivity;
    const out = step(s, course, cursor, net, net < 0n ? -net : net, refSupply, m, h);
    const v = score(out.crossings, goals, out.state, s.overheatSteps > 0n) - Math.abs(Number(u)) * 0.05 + 0.9 * search(out.state, out.cursor, depth - 1);
    if (v > best) {
      best = v;
      bestU = u;
    }
  }
  return bestU;
}

/** Net executed quantity that produces exactly lateral input `u` (before clamping). */
export function netForInput(u: bigint, refSupply: bigint, m: MovementParams): bigint {
  return (u * (refSupply / 1_000_000n)) / m.sensitivity;
}

export { Route };
